/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Util" />
import { effects } from "redux-saga";
import { SuitesActions } from "Redux/Suites/SuitesActions";
import { TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux//TelemetryActions";
import { TestHubRefreshActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import * as Contracts from "TestPlanCommon/Contracts/TestPlan";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";

/**
 * Wraps the given saga so that error handling can be done generically in the root saga. Every saga can still override this behavior by having its own try/catch within it.
 * Use for sagas that get forked (effects.fork, effects.takeEvery, effects.takeLeading, etcetera).
 */
export function errorHandledSaga(
    saga: (...args: any[]) => IterableIterator<effects.Effect>
): (...args: any[]) => IterableIterator<effects.Effect> {
    // The "any number of args" type definition for 'effects.call' requires 7 args, then the rest
    return function* (
        arg1: unknown,
        arg2: unknown,
        arg3: unknown,
        arg4: unknown,
        arg5: unknown,
        arg6: unknown,
        ...rest
    ): IterableIterator<effects.Effect> {
        try {
            yield effects.call(saga, arg1, arg2, arg3, arg4, arg5, arg6, rest);
        } catch (e) {
            yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
            const telemetryPayload = {
                errorMessage: e.message,
                saga: (arg1 as any).type
            };
            yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.SAGA_ERROR, telemetryPayload));
            const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
            if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.EnableToastNotificationsForSagaErrorFF, false)) {
                yield effects.put(SuitesActions.showToastNotifications(e.message, TestHubRefreshConstants.ERROR_TOAST_DURATION));
            }
        }
    };
}

export function getDataProviderContent(pageContext: IVssPageContext): Contracts.TestPlansHubRefreshData {
    const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const data = contributionService.getData<Contracts.TestPlansHubRefreshData>(
        "ms.vss-test-web.test-plans-hub-refresh-data-provider"
    ) as Contracts.TestPlansHubRefreshData;

    return data;
}

export function getSelectionSuiteIndexFromSuiteId(
    id: number,
    suites: Contracts.TestSuite[],
    index: number,
    idToTreeItemExpandableStateMap: Map<number, boolean>
): { index: number; found: boolean } {
    for (let i = 0; i < suites.length; i++) {
        if (suites[i].id === id) {
            index++;
            return { index: index, found: true };
        }
        index++;
        if (idToTreeItemExpandableStateMap.get(suites[i].id)) {
            const returnVal = getSelectionSuiteIndexFromSuiteId(id, suites[i].children, index, idToTreeItemExpandableStateMap);
            index = returnVal.index;
            if (returnVal.found) {
                return { index: index, found: true };
            }
        }
    }
    return { index: index, found: false };
}

export function getSuiteIdFromSelectedSuiteIndex(
    index: number,
    suites: Contracts.TestSuite[],
    idToTreeItemExpandableStateMap: Map<number, boolean>
): { suiteId: number; index: number } {
    for (let i = 0; i < suites.length; i++) {
        if (index === 0) {
            return { suiteId: suites[i].id, index: index };
        }
        index--;
        if (idToTreeItemExpandableStateMap.get(suites[i].id)) {
            const returnVal = getSuiteIdFromSelectedSuiteIndex(index, suites[i].children, idToTreeItemExpandableStateMap);
            index = returnVal.index;
            if (returnVal.suiteId) {
                return { suiteId: returnVal.suiteId, index: index };
            }
        }
    }
    return { suiteId: 0, index: index };
}

// Method to show a user-friendly error message on a toast bubble. Also logs the error message sent from the server to the console as an error.
export function showErrorToast(pageContext: IVssPageContext, message: string, errorMessage?: string, callToActionText?: string, onCallToActionClick?: () => void) {
    const globalMessageService: IGlobalMessagesService = pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");

    let duration: number;

    if (callToActionText) {
        // For an error toast, if there is an action button present we want the timeout to be 10s.
        duration = 10000;
    }
    else {
        // Otherwise it should be 5s.
        duration = 5000;
    }

    globalMessageService.addToast({
        duration: duration,
        message: message,
        callToAction: callToActionText,
        onCallToActionClick: onCallToActionClick
    });

    if (errorMessage) {
        console.error(errorMessage);
    }
}

export function checkIfIsIE(): boolean {
    var ua = window.navigator.userAgent;

    // For IE 10 and below.
    var msie = ua.indexOf('MSIE ');
    if (msie > 0) {
        return true;
    }

    // For IE 11.
    var trident = ua.indexOf('Trident/');
    if (trident > 0) {
        return true;
    }

    return false;
}

export function compareColumnWidthsMaps(map1: { [columnId: string]: number }, map2: { [columnId: string]: number }) {
    if (map1 && map2) {
        if (map1.size !== map2.size) {
            return false;
        }
        for (let key in map1) {
            let value1 = map1[key];
            if (!map2[key]) {
                return false;
            }
            let value2 = map2[key];
            if (value1 !== value2) {
                return false;
            }
        }
        return true;
    }
    //To ensure that they are considered as equal even when both are undefined
    else if (!map1 && !map2) {
        return true;
    }
    else {
        return false;
    }
}

export function getSelectedBuildObject(buildId: string) {
    let selectedBuildId: string = "0";
    let buildUri = "";
    if (buildId && buildId !== "0") {
        selectedBuildId = buildId;
        buildUri = "vstfs:///Build/Build/" + buildId;
    }
    return { url: Utils_String.format(buildUri, selectedBuildId), id: selectedBuildId, name: "" };
}

export function setTestPlanPageTitle(pageContext: IVssPageContext, pageTitle: string): void {
    const navigationService = pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
    navigationService.setPageTitle(pageTitle);
}